import { supabase } from '../lib/supabase';

/**
 * Fetch owner's bookings with real-time updates
 * @param {string} ownerId - Owner user ID
 * @returns {Promise<Array>} Array of bookings
 */
export const fetchOwnerBookings = async (ownerId) => {
  try {
    const { data, error } = await supabase
      ?.from('bookings')
      ?.select(`
        *,
        renter:user_profiles!bookings_renter_id_fkey(
          id,
          full_name,
          email,
          phone,
          profile_photo_url
        ),
        trailer:trailer_listings!bookings_trailer_listing_id_fkey(
          id,
          title,
          trailer_type,
          photos
        )
      `)
      ?.eq('owner_id', ownerId)
      ?.order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching owner bookings:', error?.message);
      return [];
    }

    return data?.map(convertToCamelCase) || [];
  } catch (error) {
    console.error('Error in fetchOwnerBookings:', error);
    return [];
  }
};

/**
 * Fetch renter's bookings with trailer and owner details
 * @param {string} renterId - Renter user ID
 * @returns {Promise<Array>} Array of bookings
 */
export const fetchRenterBookings = async (renterId) => {
  try {
    const { data, error } = await supabase
      ?.from('bookings')
      ?.select(`
        *,
        owner:user_profiles!bookings_owner_id_fkey(
          id,
          full_name,
          email,
          phone,
          profile_photo_url
        ),
        trailer:trailer_listings!bookings_trailer_listing_id_fkey(
          id,
          title,
          trailer_type,
          location_city,
          location_state,
          photos
        )
      `)
      ?.eq('renter_id', renterId)
      ?.order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching renter bookings:', error?.message);
      return [];
    }

    return data?.map(convertToCamelCase) || [];
  } catch (error) {
    console.error('Error in fetchRenterBookings:', error);
    return [];
  }
};

/**
 * Fetch active bookings for renter (confirmed or pending)
 * @param {string} renterId - Renter user ID
 * @returns {Promise<Array>} Array of active bookings
 */
export const fetchActiveRentals = async (renterId) => {
  try {
    const { data, error } = await supabase
      ?.from('bookings')
      ?.select(`
        *,
        owner:user_profiles!bookings_owner_id_fkey(
          id,
          full_name,
          email,
          phone
        ),
        trailer:trailer_listings!bookings_trailer_listing_id_fkey(
          id,
          title,
          trailer_type,
          location_city,
          location_state,
          photos
        )
      `)
      ?.eq('renter_id', renterId)
      ?.in('status', ['confirmed', 'pending'])
      ?.order('start_date', { ascending: true });

    if (error) {
      console.error('Error fetching active rentals:', error?.message);
      return [];
    }

    return data?.map(convertToCamelCase) || [];
  } catch (error) {
    console.error('Error in fetchActiveRentals:', error);
    return [];
  }
};

/**
 * Fetch rental history for renter (completed bookings)
 * @param {string} renterId - Renter user ID
 * @returns {Promise<Array>} Array of completed bookings
 */
export const fetchRentalHistory = async (renterId) => {
  try {
    const { data, error } = await supabase
      ?.from('bookings')
      ?.select(`
        *,
        trailer:trailer_listings!bookings_trailer_listing_id_fkey(
          id,
          title,
          trailer_type,
          location_city,
          location_state,
          photos
        )
      `)
      ?.eq('renter_id', renterId)
      ?.eq('status', 'completed')
      ?.order('end_date', { ascending: false });

    if (error) {
      console.error('Error fetching rental history:', error?.message);
      return [];
    }

    return data?.map(convertToCamelCase) || [];
  } catch (error) {
    console.error('Error in fetchRentalHistory:', error);
    return [];
  }
};

/**
 * Fetch completed bookings for a user (owner or renter)
 * @param {string} userId - User ID
 * @param {string} role - User role (owner or renter)
 * @returns {Promise<Array>} Array of completed bookings
 */
export const fetchCompletedBookings = async (userId, role) => {
  try {
    let query = supabase
      ?.from('bookings')
      ?.select(`
        *,
        renter:user_profiles!bookings_renter_id_fkey(
          id,
          full_name,
          email,
          phone,
          profile_photo_url
        ),
        owner:user_profiles!bookings_owner_id_fkey(
          id,
          full_name,
          email,
          phone,
          profile_photo_url
        ),
        trailer:trailer_listings!bookings_trailer_listing_id_fkey(
          id,
          title,
          trailer_type,
          location_city,
          location_state,
          photos
        )
      `)
      ?.eq('status', 'completed');

    if (role === 'owner') {
      query = query?.eq('owner_id', userId);
    } else {
      query = query?.eq('renter_id', userId);
    }

    const { data, error } = await query?.order('end_date', { ascending: false });

    if (error) {
      console.error('Error fetching completed bookings:', error?.message);
      return [];
    }

    return data?.map(convertToCamelCase) || [];
  } catch (error) {
    console.error('Error in fetchCompletedBookings:', error);
    return [];
  }
};

/**
 * Calculate owner earnings from bookings
 * @param {Array} bookings - Array of bookings
 * @returns {Object} Earnings data
 */
export const calculateEarnings = (bookings) => {
  const completedBookings = bookings?.filter(b => b?.status === 'completed' && b?.paymentStatus === 'succeeded') || [];
  
  const totalRevenue = completedBookings?.reduce((sum, booking) => {
    const platformFee = parseFloat(booking?.totalAmount || 0) * 0.15;
    return sum + (parseFloat(booking?.totalAmount || 0) - platformFee);
  }, 0);

  const currentMonth = new Date()?.getMonth();
  const currentYear = new Date()?.getFullYear();
  
  const monthlyRevenue = completedBookings
    ?.filter(b => {
      const bookingDate = new Date(b?.createdAt);
      return bookingDate?.getMonth() === currentMonth && bookingDate?.getFullYear() === currentYear;
    })
    ?.reduce((sum, booking) => {
      const platformFee = parseFloat(booking?.totalAmount || 0) * 0.15;
      return sum + (parseFloat(booking?.totalAmount || 0) - platformFee);
    }, 0);

  // Calculate monthly data for last 6 months
  const monthlyData = [];
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  
  for (let i = 5; i >= 0; i--) {
    const targetDate = new Date(currentYear, currentMonth - i, 1);
    const targetMonth = targetDate?.getMonth();
    const targetYear = targetDate?.getFullYear();
    
    const monthRevenue = completedBookings
      ?.filter(b => {
        const bookingDate = new Date(b?.createdAt);
        return bookingDate?.getMonth() === targetMonth && bookingDate?.getFullYear() === targetYear;
      })
      ?.reduce((sum, booking) => {
        const platformFee = parseFloat(booking?.totalAmount || 0) * 0.15;
        return sum + (parseFloat(booking?.totalAmount || 0) - platformFee);
      }, 0);
    
    monthlyData?.push({
      month: monthNames?.[targetMonth],
      revenue: Math.round(monthRevenue)
    });
  }

  return {
    monthlyRevenue: Math.round(monthlyRevenue),
    totalBookings: bookings?.length || 0,
    completedBookings: completedBookings?.length || 0,
    monthlyData
  };
};

/**
 * Fetch payout transactions for owner
 * @param {string} userId - Owner user ID
 * @returns {Promise<Object>} Payout data
 */
export const fetchPayoutData = async (userId) => {
  try {
    const { data, error } = await supabase
      ?.from('payout_transactions')
      ?.select('*')
      ?.eq('user_id', userId)
      ?.order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching payout data:', error?.message);
      return { pendingPayouts: 0, transactions: [] };
    }

    const pendingPayouts = data
      ?.filter(t => t?.payout_status === 'pending')
      ?.reduce((sum, t) => sum + parseFloat(t?.net_amount || 0), 0);

    return {
      pendingPayouts: Math.round(pendingPayouts),
      transactions: data?.map(convertToCamelCase) || []
    };
  } catch (error) {
    console.error('Error in fetchPayoutData:', error);
    return { pendingPayouts: 0, transactions: [] };
  }
};

/**
 * Update booking status (accept/decline/cancel)
 * @param {string} bookingId - Booking ID
 * @param {string} newStatus - New status ('confirmed', 'cancelled', 'completed')
 * @returns {Promise<Object>} Updated booking
 */
export const updateBookingStatus = async (bookingId, newStatus) => {
  try {
    const { data, error } = await supabase
      ?.from('bookings')
      ?.update({ status: newStatus, updated_at: new Date()?.toISOString() })
      ?.eq('id', bookingId)
      ?.select()
      ?.single();

    if (error) {
      console.error('Error updating booking status:', error?.message);
      throw error;
    }

    return convertToCamelCase(data);
  } catch (error) {
    console.error('Error in updateBookingStatus:', error);
    throw error;
  }
};

/**
 * Convert snake_case to camelCase
 */
function convertToCamelCase(obj) {
  if (!obj) return obj;
  if (Array.isArray(obj)) return obj?.map(convertToCamelCase);
  if (typeof obj !== 'object') return obj;

  const camelCaseObj = {};
  for (const [key, value] of Object.entries(obj)) {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    camelCaseObj[camelKey] = typeof value === 'object' ? convertToCamelCase(value) : value;
  }
  return camelCaseObj;
}

export const bookingService = {
  fetchOwnerBookings,
  fetchRenterBookings,
  fetchActiveRentals,
  fetchRentalHistory,
  fetchCompletedBookings,
  calculateEarnings,
  fetchPayoutData,
  updateBookingStatus
};